import { useCallback, useEffect, useState, type CSSProperties, type FormEvent } from "react";
import type { User } from "@shared/core/types";
import {
  DEFAULT_WORKDAYS,
  WEEKDAY_SHORT_LABELS,
  formatCad,
  type Technician,
  type WeeklyHours,
  type TimeOff,
} from "@shared/modules/x-atelier";
import { api } from "../../../core/lib/api";
import { useToastStore } from "../../../core/stores/toast";
import { Badge } from "../../../core/components/ui/badge";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Field, Input, Select } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import { PageHeader } from "../../../core/components/ui/page-header";
import {
  createTechnician,
  createTimeOff,
  deleteTechnician,
  deleteTimeOff,
  listTechnicians,
  listTimeOff,
  updateTechnician,
} from "../lib/api";
import { formatDayShort } from "../lib/dates";
import { centsToInput, parseAmountToCents } from "../lib/money";

const FORM_ID = "atelier-technician-form";
const DEFAULT_COLOR = "#f5b800";

interface Draft {
  displayName: string;
  color: string;
  phone: string;
  hourlyRate: string;
  userId: string;
  active: boolean;
  workdays: number[];
  weeklyHours: WeeklyHours | null;
}

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

function draftFrom(t?: Technician): Draft {
  return {
    displayName: t?.displayName ?? "",
    color: t?.color ?? DEFAULT_COLOR,
    phone: t?.phone ?? "",
    hourlyRate: centsToInput(t?.hourlyRateCents ?? null),
    userId: t?.userId ?? "",
    active: t?.active ?? true,
    workdays: t?.workdays ?? DEFAULT_WORKDAYS,
    weeklyHours: t?.weeklyHours ?? null,
  };
}

/** Équipe (admin) : techniciens, couleur d'agenda, taux horaire, compte lié. */
export function EquipePage() {
  const push = useToastStore((s) => s.push);
  const [list, setList] = useState<Technician[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [modal, setModal] = useState<{ open: boolean; technician?: Technician }>({ open: false });
  const [draft, setDraft] = useState<Draft>(() => draftFrom());
  const [confirm, setConfirm] = useState<Technician | null>(null);
  const [busy, setBusy] = useState(false);
  // Absences du technicien ouvert dans la fenêtre dédiée.
  const [absences, setAbsences] = useState<{ technician: Technician; items: TimeOff[] } | null>(
    null,
  );
  const [absenceDraft, setAbsenceDraft] = useState({
    fromDate: "",
    toDate: "",
    fromTime: "",
    toTime: "",
    label: "",
  });

  const openAbsences = async (technician: Technician) => {
    try {
      setAbsences({ technician, items: await listTimeOff(technician.id) });
      setAbsenceDraft({ fromDate: "", toDate: "", fromTime: "", toTime: "", label: "" });
    } catch {
      push("danger", "Absences indisponibles");
    }
  };

  const addAbsence = async (e: FormEvent) => {
    e.preventDefault();
    if (!absences || busy || !absenceDraft.fromDate) return;
    setBusy(true);
    try {
      const created = await createTimeOff(absences.technician.id, {
        fromDate: absenceDraft.fromDate,
        toDate: absenceDraft.toDate || absenceDraft.fromDate,
        fromTime: absenceDraft.fromTime || null,
        toTime: absenceDraft.toTime || null,
        label: absenceDraft.label.trim() || null,
      });
      setAbsences({
        ...absences,
        items: [...absences.items, created].sort((a, b) => a.fromDate.localeCompare(b.fromDate)),
      });
      setAbsenceDraft({ fromDate: "", toDate: "", fromTime: "", toTime: "", label: "" });
      push("success", "Absence ajoutée", "Les créneaux en ligne en tiennent compte.");
    } catch {
      push("danger", "Ajout impossible", "Vérifiez les dates.");
    } finally {
      setBusy(false);
    }
  };

  const removeAbsence = async (item: TimeOff) => {
    if (!absences || busy) return;
    setBusy(true);
    try {
      await deleteTimeOff(item.id);
      setAbsences({ ...absences, items: absences.items.filter((i) => i.id !== item.id) });
    } catch {
      push("danger", "Suppression impossible");
    } finally {
      setBusy(false);
    }
  };

  const load = useCallback(() => {
    listTechnicians()
      .then((l) => {
        setList(l);
        setFailed(false);
      })
      .catch(() => {
        setList([]);
        setFailed(true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  useEffect(() => {
    api<User[]>("/api/users")
      .then(setUsers)
      .catch(() => setUsers([]));
  }, []);

  const open = (technician?: Technician) => {
    setDraft(draftFrom(technician));
    setModal({ open: true, technician });
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const displayName = draft.displayName.trim();
    if (!displayName || busy) return;
    const hourlyRateCents = draft.hourlyRate.trim() ? parseAmountToCents(draft.hourlyRate) : null;
    if (draft.hourlyRate.trim() && hourlyRateCents === null) {
      push("danger", "Taux horaire illisible", "Saisissez un montant, par exemple 32,00.");
      return;
    }
    if (!/^#[0-9a-fA-F]{6}$/.test(draft.color)) {
      push("danger", "Couleur invalide", "Format attendu : #rrggbb.");
      return;
    }
    const input = {
      displayName,
      color: draft.color,
      phone: draft.phone.trim() || null,
      hourlyRateCents,
      userId: draft.userId || null,
      active: draft.active,
      workdays: draft.workdays,
      weeklyHours: draft.weeklyHours,
    };
    setBusy(true);
    try {
      const t = modal.technician;
      if (t) await updateTechnician(t.id, input);
      else await createTechnician(input);
      push("success", t ? "Technicien mis à jour" : "Technicien ajouté", displayName);
      setModal({ open: false });
      load();
    } catch {
      push("danger", "Enregistrement impossible", "Vérifiez les champs saisis.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!confirm || busy) return;
    setBusy(true);
    try {
      await deleteTechnician(confirm.id);
      push("success", "Technicien supprimé");
      setConfirm(null);
      load();
    } catch {
      push("danger", "Suppression impossible");
    } finally {
      setBusy(false);
    }
  };

  const userName = (id: string | null) =>
    id ? (users.find((u) => u.id === id)?.name ?? "Compte inconnu") : null;

  return (
    <div className="page equipe">
      <PageHeader
        title="Équipe"
        subtitle="Les techniciens de l'atelier, leur couleur d'agenda et leur taux horaire."
        actions={
          <Button variant="primary" icon={<Icon name="plus" size={16} />} onClick={() => open()}>
            Nouveau technicien
          </Button>
        }
      />

      <table className="table">
        <thead>
          <tr>
            <th>Technicien</th>
            <th>Téléphone</th>
            <th>Présence</th>
            <th className="equipe__num">Taux horaire</th>
            <th>Compte lié</th>
            <th>Actif</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {list.map((t) => (
            <tr key={t.id}>
              <td>
                <span className="equipe__name">
                  <span
                    className="equipe__swatch"
                    style={{ "--tech-color": t.color } as CSSProperties}
                    aria-hidden="true"
                  />
                  {t.displayName}
                </span>
              </td>
              <td>{t.phone ?? "—"}</td>
              <td>
                <span className="equipe__days" aria-label="Jours de présence">
                  {WEEKDAYS.map((d) => (
                    <span
                      key={d}
                      className={`equipe__day${t.workdays.includes(d) ? " is-on" : ""}`}
                      title={WEEKDAY_SHORT_LABELS[d]}
                    >
                      {WEEKDAY_SHORT_LABELS[d]!.charAt(0)}
                    </span>
                  ))}
                </span>
              </td>
              <td className="equipe__num">
                {t.hourlyRateCents !== null && t.hourlyRateCents !== undefined
                  ? `${formatCad(t.hourlyRateCents)} / h`
                  : "—"}
              </td>
              <td>{userName(t.userId) ?? <span className="equipe__muted">Aucun</span>}</td>
              <td>
                {t.active ? (
                  <Badge variant="success">Actif</Badge>
                ) : (
                  <Badge variant="neutral">Inactif</Badge>
                )}
              </td>
              <td className="table__actions">
                <Button variant="ghost" size="sm" onClick={() => void openAbsences(t)}>
                  Absences
                </Button>{" "}
                <Button variant="ghost" size="sm" onClick={() => open(t)}>
                  Modifier
                </Button>{" "}
                <Button variant="ghost-danger" size="sm" onClick={() => setConfirm(t)}>
                  Supprimer
                </Button>
              </td>
            </tr>
          ))}
          {!loading && list.length === 0 ? (
            <tr>
              <td colSpan={7} className="table__empty">
                {failed
                  ? "Liste indisponible — réessayez dans un instant."
                  : "Aucun technicien : ajoutez-en un pour colorer l'agenda."}
              </td>
            </tr>
          ) : null}
          {loading ? (
            <tr>
              <td colSpan={7} className="table__empty">
                Chargement…
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <Modal
        open={modal.open}
        title={modal.technician ? "Modifier le technicien" : "Nouveau technicien"}
        onClose={() => setModal({ open: false })}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setModal({ open: false })} disabled={busy}>
              Annuler
            </Button>
            <Button
              type="submit"
              form={FORM_ID}
              variant="primary"
              disabled={busy || !draft.displayName.trim()}
            >
              {modal.technician ? "Enregistrer" : "Ajouter"}
            </Button>
          </>
        }
      >
        <form id={FORM_ID} className="atelier-form" onSubmit={(e) => void save(e)}>
          <div className="form-section__grid">
            <Field label="Nom affiché">
              <Input
                value={draft.displayName}
                onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
                placeholder="ex : Maxime"
                autoFocus
                required
              />
            </Field>
            <Field label="Couleur d'agenda">
              <Input
                type="color"
                className="equipe__color"
                value={draft.color}
                onChange={(e) => setDraft({ ...draft, color: e.target.value })}
              />
            </Field>
            <Field label="Téléphone">
              <Input
                type="tel"
                value={draft.phone}
                onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
              />
            </Field>
            <Field label="Taux horaire ($)" hint="Visible des administrateurs seulement.">
              <Input
                inputMode="decimal"
                value={draft.hourlyRate}
                onChange={(e) => setDraft({ ...draft, hourlyRate: e.target.value })}
                placeholder="0,00"
              />
            </Field>
            <Field label="Compte lié" hint="Facultatif : un technicien peut ne pas avoir d'accès.">
              <Select
                value={draft.userId}
                onChange={(e) => setDraft({ ...draft, userId: e.target.value })}
              >
                <option value="">Aucun compte</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.email})
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <fieldset className="atelier-subsection">
            <legend className="atelier-subsection__title">Jours de présence</legend>
            <p className="atelier-subsection__hint">
              La réservation en ligne n'offre un créneau que si un technicien est présent ce
              jour-là.
            </p>
            <label className="atelier-check">
              <input
                type="checkbox"
                checked={draft.weeklyHours !== null}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    weeklyHours: e.target.checked
                      ? Object.fromEntries(
                          draft.workdays.map((d) => [
                            String(d),
                            [{ open: "08:00", close: "17:00" }],
                          ]),
                        )
                      : null,
                  })
                }
              />
              Définir les plages de chaque journée (pauses et demi-journées)
            </label>
            {draft.weeklyHours !== null ? (
              WEEKDAYS.map((d) => (
                <div key={d} className="atelier-subsection">
                  <strong>{WEEKDAY_SHORT_LABELS[d]}</strong>
                  {(draft.weeklyHours![String(d)] ?? []).map((slot, i) => (
                    <div className="form-section__grid" key={i}>
                      <Field label="Début">
                        <Input
                          type="time"
                          required
                          value={slot.open}
                          onChange={(e) =>
                            setDraft({
                              ...draft,
                              weeklyHours: {
                                ...draft.weeklyHours,
                                [d]: draft.weeklyHours![d]!.map((x, j) =>
                                  j === i ? { ...x, open: e.target.value } : x,
                                ),
                              },
                            })
                          }
                        />
                      </Field>
                      <Field label="Fin">
                        <Input
                          type="time"
                          required
                          value={slot.close}
                          onChange={(e) =>
                            setDraft({
                              ...draft,
                              weeklyHours: {
                                ...draft.weeklyHours,
                                [d]: draft.weeklyHours![d]!.map((x, j) =>
                                  j === i ? { ...x, close: e.target.value } : x,
                                ),
                              },
                            })
                          }
                        />
                      </Field>
                      <Button
                        type="button"
                        variant="ghost-danger"
                        onClick={() =>
                          setDraft({
                            ...draft,
                            weeklyHours: {
                              ...draft.weeklyHours,
                              [d]: draft.weeklyHours![d]!.filter((_, j) => j !== i),
                            },
                          })
                        }
                      >
                        Retirer la plage
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={(draft.weeklyHours![d]?.length ?? 0) >= 4}
                    onClick={() =>
                      setDraft({
                        ...draft,
                        weeklyHours: {
                          ...draft.weeklyHours,
                          [d]: [
                            ...(draft.weeklyHours![d] ?? []),
                            { open: "08:00", close: "12:00" },
                          ],
                        },
                      })
                    }
                  >
                    Ajouter une plage
                  </Button>
                </div>
              ))
            ) : (
              <div className="equipe__workdays">
                {WEEKDAYS.map((d) => (
                  <label
                    key={d}
                    className={`chips__chip${draft.workdays.includes(d) ? " is-active" : ""}`}
                  >
                    <input
                      type="checkbox"
                      className="equipe__workday-input"
                      checked={draft.workdays.includes(d)}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          workdays: e.target.checked
                            ? [...draft.workdays, d].sort()
                            : draft.workdays.filter((x) => x !== d),
                        })
                      }
                    />
                    {WEEKDAY_SHORT_LABELS[d]}
                  </label>
                ))}
              </div>
            )}
          </fieldset>
          <label className="atelier-check">
            <input
              type="checkbox"
              checked={draft.active}
              onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
            />
            Actif (proposé dans l'agenda)
          </label>
        </form>
      </Modal>

      <Modal
        open={absences !== null}
        title={absences ? `Absences — ${absences.technician.displayName}` : "Absences"}
        onClose={() => setAbsences(null)}
        footer={
          <Button variant="ghost" onClick={() => setAbsences(null)}>
            Fermer
          </Button>
        }
      >
        {absences ? (
          <div className="atelier-form">
            {absences.items.length === 0 ? (
              <p className="equipe__muted">Aucune absence prévue.</p>
            ) : (
              <ul className="equipe__absences">
                {absences.items.map((item) => (
                  <li key={item.id} className="equipe__absence">
                    <span>
                      <strong>
                        {item.fromDate === item.toDate
                          ? formatDayShort(`${item.fromDate}T12:00:00`)
                          : `${formatDayShort(`${item.fromDate}T12:00:00`)} → ${formatDayShort(`${item.toDate}T12:00:00`)}`}
                      </strong>
                      {item.fromTime ? (
                        <span>
                          {" "}
                          · {item.fromTime} – {item.toTime}
                        </span>
                      ) : null}
                      {item.label ? <span className="equipe__muted"> · {item.label}</span> : null}
                    </span>
                    <Button
                      variant="ghost-danger"
                      size="sm"
                      disabled={busy}
                      onClick={() => void removeAbsence(item)}
                    >
                      Retirer
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <form className="form-section__grid" onSubmit={(e) => void addAbsence(e)}>
              <Field label="Du">
                <Input
                  type="date"
                  value={absenceDraft.fromDate}
                  onChange={(e) => setAbsenceDraft({ ...absenceDraft, fromDate: e.target.value })}
                  required
                />
              </Field>
              <Field label="Au" hint="Vide : une seule journée.">
                <Input
                  type="date"
                  value={absenceDraft.toDate}
                  min={absenceDraft.fromDate}
                  onChange={(e) => setAbsenceDraft({ ...absenceDraft, toDate: e.target.value })}
                />
              </Field>
              <Field
                label="Heure de début"
                hint="Laisser les deux heures vides pour une absence toute la journée."
              >
                <Input
                  type="time"
                  value={absenceDraft.fromTime}
                  onChange={(e) => setAbsenceDraft({ ...absenceDraft, fromTime: e.target.value })}
                />
              </Field>
              <Field label="Heure de fin">
                <Input
                  type="time"
                  value={absenceDraft.toTime}
                  onChange={(e) => setAbsenceDraft({ ...absenceDraft, toTime: e.target.value })}
                />
              </Field>
              <Field label="Motif">
                <Input
                  value={absenceDraft.label}
                  onChange={(e) => setAbsenceDraft({ ...absenceDraft, label: e.target.value })}
                  placeholder="ex : Vacances"
                />
              </Field>
              <div className="equipe__absence-submit">
                <Button type="submit" variant="primary" disabled={busy || !absenceDraft.fromDate}>
                  Ajouter l'absence
                </Button>
              </div>
            </form>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={confirm !== null}
        title="Supprimer ce technicien ?"
        onClose={() => setConfirm(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              Annuler
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => void remove()}>
              Supprimer
            </Button>
          </>
        }
      >
        <p>
          {confirm?.displayName} sera retiré de l'équipe ; ses rendez-vous restent, sans technicien
          attribué.
        </p>
      </Modal>
    </div>
  );
}
